import { useEffect, useState, useRef } from "react";
import { Loader2 } from "lucide-react";
import IntellendLogo from "@/components/IntellendLogo";

interface FullScreenLoaderProps {
  loading: boolean;
  minDuration?: number; // default 500ms
  message?: string | null; // if set → BRE animated mode with rotating messages
}

// Messages shown one at a time during BRE / lender evaluation
const BRE_MESSAGES = [
  "Collecting your documents...",
  "Connecting with the lender...",
  "Evaluating your loan eligibility...",
  "Exploring the best offer for you...",
  "Checking your credit profile...",
  "Almost there, hang tight...",
  "This may take up to 2 minutes...",
  "Please do not refresh the page...",
  "Finalising your personalised offer...",
];

// Messages shown after Additional Details submission
const ADDITIONAL_MESSAGES = [
  "Verifying your info...",
  "Matching you with lenders...",
  "Generating best offer for you...",
  "Almost there, hang tight...",
];

// Messages shown while confirming bank account & offer
const BANK_CONFIRM_MESSAGES = [
  "Confirming your offer...",
  "Saving your bank details...",
  "Setting up your account...",
  "Almost there...",
];

// Messages shown while setting up UPI / NACH mandate
const MANDATE_MESSAGES = [
  "Setting up your repayment mandate...",
  "Connecting with your bank...",
  "Please complete the steps in the popup...",
  "Waiting for mandate confirmation...",
];

// Messages shown while initiating eSign
const ESIGN_MESSAGES = [
  "Preparing your loan agreement...",
  "Initiating digital signature...",
  "Please sign in the popup window...",
  "Waiting for your signature...",
];

// Messages shown after eSign success (advancing journey)
const ESIGN_COMPLETE_MESSAGES = [
  "Signature received!",
  "Saving your signed agreement...",
  "Advancing your application...",
  "Almost there...",
];

// Messages shown while pushing documents to lender
const DOC_PUSH_MESSAGES = [
  "Submitting your application...",
  "Pushing verified documents to lender...",
  "Finalising your loan application...",
  "Almost there, hang tight...",
];

const MESSAGE_INTERVAL_MS = 7000; // how long each message is shown
const FADE_DURATION_MS = 600;     // CSS fade duration (ms)

export default function FullScreenLoader({
  loading,
  minDuration = 500,
  message,
}: FullScreenLoaderProps) {
  const [visible, setVisible] = useState(false);
  const startTimeRef = useRef<number | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pick the right messages array based on the message prop
  const activeMessages =
    message === "additional"    ? ADDITIONAL_MESSAGES    :
    message === "bank-confirm"  ? BANK_CONFIRM_MESSAGES  :
    message === "mandate"       ? MANDATE_MESSAGES       :
    message === "esign"         ? ESIGN_MESSAGES         :
    message === "esign-complete"? ESIGN_COMPLETE_MESSAGES:
    message === "doc-push"      ? DOC_PUSH_MESSAGES      :
    BRE_MESSAGES;

  // Current message text + fade state
  const [currentMsg, setCurrentMsg] = useState(activeMessages[0]);
  const [fadeIn, setFadeIn] = useState(true);
  const msgIndexRef = useRef(0);
  const msgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Progress bar (0–92%)
  const [progress, setProgress] = useState(0);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Advance to next message with fade-out → swap text → fade-in
  const advanceMessage = (msgs: string[]) => {
    setFadeIn(false);
    setTimeout(() => {
      msgIndexRef.current = (msgIndexRef.current + 1) % msgs.length;
      setCurrentMsg(msgs[msgIndexRef.current]);
      setFadeIn(true);
    }, FADE_DURATION_MS);
  };

  // Start / stop message cycling and progress bar
  useEffect(() => {
    const msgs = activeMessages;
    if (visible && message) {
      msgIndexRef.current = 0;
      setCurrentMsg(msgs[0]);
      setFadeIn(true);
      setProgress(0);

      const scheduleNext = () => {
        msgTimerRef.current = setTimeout(() => {
          advanceMessage(msgs);
          scheduleNext();
        }, MESSAGE_INTERVAL_MS);
      };
      scheduleNext();

      progressIntervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 92) return prev;
          const inc = prev < 40 ? 3 : prev < 70 ? 1.5 : 0.4;
          return Math.min(prev + inc, 92);
        });
      }, 600);
    } else {
      if (msgTimerRef.current) clearTimeout(msgTimerRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (!visible) {
        setProgress(0);
        msgIndexRef.current = 0;
        setCurrentMsg(msgs[0]);
        setFadeIn(true);
      }
    }

    return () => {
      if (msgTimerRef.current) clearTimeout(msgTimerRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [visible, message]);

  // Show / hide with minDuration
  useEffect(() => {
    if (loading) {
      if (!visible) setVisible(true);
      if (!startTimeRef.current) startTimeRef.current = Date.now();
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    } else if (startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(minDuration - elapsed, 0);
      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
        startTimeRef.current = null;
        hideTimerRef.current = null;
      }, remaining);
    }
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [loading, minDuration, visible]);

  if (!visible) return null;

  // ── BRE / long-wait animated mode ────────────────────────────────────────
  if (message) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
        <div className="mx-4 w-full max-w-sm rounded-2xl bg-intellend-white shadow-2xl overflow-hidden">

          {/* Intellend-green progress bar */}
          <div className="h-1.5 w-full bg-gray-100">
            <div
              className="h-full transition-all duration-700 ease-out"
              style={{
                width: `${progress}%`,
                background: "linear-gradient(to right, #065a4e, #0a7a6a)",
              }}
            />
          </div>

          <div className="px-8 py-7 text-center">
            {/* Intellend logo */}
            <div className="flex justify-center mb-5">
              <IntellendLogo size="md" />
            </div>

            {/* Spinner with pulsing ring — brand green */}
            <div className="flex justify-center mb-5">
              <div className="relative">
                <div
                  className="h-14 w-14 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "#e6f0ee" }}
                >
                  <Loader2
                    className="h-7 w-7 animate-spin"
                    style={{ color: "#065a4e" }}
                  />
                </div>
                {/* Pulsing ring */}
                <span
                  className="absolute inset-0 rounded-full animate-ping opacity-30"
                  style={{ backgroundColor: "#065a4e" }}
                />
              </div>
            </div>

            {/* Single message — fades out then in on each change */}
            <div className="min-h-[28px] mb-1">
              <p
                className="text-base font-semibold text-gray-800 transition-opacity"
                style={{
                  opacity: fadeIn ? 1 : 0,
                  transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
                }}
              >
                {currentMsg}
              </p>
            </div>

            <p className="text-xs text-gray-400 mt-3 mb-5">
              Do not close or refresh this page
            </p>

            {/* Progress dots — active dot stretches, brand green */}
            <div className="flex justify-center gap-1.5">
              {activeMessages.map((_, i) => (
                <span
                  key={i}
                  className="block h-1.5 rounded-full transition-all duration-500"
                  style={{
                    width: i === msgIndexRef.current ? "20px" : "6px",
                    backgroundColor:
                      i === msgIndexRef.current ? "#065a4e" : "#d1d5db",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Default simple loader ─────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-md">
      <div className="flex flex-col items-center space-y-4">
        <Loader2 className="h-16 w-16 animate-spin text-white" />
        <p className="text-white text-sm">Loading, please wait...</p>
      </div>
    </div>
  );
}
